import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { FirebaseX } from '@ionic-native/firebase-x/ngx';
import { Platform } from '@ionic/angular';
import firebase from 'firebase/app';
import 'firebase/remote-config';
import { from } from 'rxjs';
import { map, share, switchMap } from 'rxjs/operators';
import { firebaseWebConfig } from '../../../google-services.web';

@Injectable({
  providedIn: 'root'
})
export class FirebaseRemoteConfigService {

  private static readonly MINIMUM_FETCH_INTERVAL = 1800;
  private isRemoteConfigFetched = false;
  private webRemoteConfig: firebase.remoteConfig.RemoteConfig;

  private fetchActivateRemoteConfigShare$ = from(this.platform.ready()).pipe(
    switchMap(() => {
      if (this.platform.is('cordova')) {
        return this.fetchActivateAppRemoteConfig();
      } else {
        return this.fetchActivateWebRemoteConfig();
      }
    }),
    share()
  );

  constructor(
    private http: HttpClient,
    private firebasex: FirebaseX,
    private platform: Platform
  ) {}

  async init() {
    await this.setDefaultConfig();
    await this.fetchActivateRemoteConfigShare$.toPromise();
  }

  async getConfigValue(configKey: string) {
    if (!this.isRemoteConfigFetched) {
      await this.fetchActivateRemoteConfigShare$.toPromise();
    }
    if (this.platform.is('cordova')) {
      const value = await this.firebasex.getValue(configKey);
      return this.buildNativeValue(value);
    } else {
      return this.webRemoteConfig.getValue(configKey);
    }
  }

  private async fetchActivateAppRemoteConfig() {
    try {
      await this.firebasex.fetch(FirebaseRemoteConfigService.MINIMUM_FETCH_INTERVAL);
      await this.firebasex.activateFetched();
      this.isRemoteConfigFetched = true;
    } catch {}
  }

  private async fetchActivateWebRemoteConfig() {
    this.initWebRemoteConfig();
    await this.webRemoteConfig?.fetchAndActivate().catch(() => {});
    this.isRemoteConfigFetched = true;
  }

  private async setDefaultConfig() {
    const defaultConfig = await this.http.get(`assets/config/firebase-default-config.json`)
      .pipe(map((json: any) => json.entries)).toPromise();
    if (this.platform.is('cordova')) {
      await this.firebasex.setDefaults(defaultConfig);
    } else {
      this.initWebRemoteConfig();
      this.webRemoteConfig.defaultConfig = defaultConfig;
    }
  }

  private initWebRemoteConfig() {
    if (this.platform.is('cordova') || !!this.webRemoteConfig) {
      return;
    }
    const app = firebase.initializeApp(firebaseWebConfig);
    this.webRemoteConfig = firebase.remoteConfig(app);
    this.webRemoteConfig.settings.minimumFetchIntervalMillis = FirebaseRemoteConfigService.MINIMUM_FETCH_INTERVAL;
  }

  private buildNativeValue(value: any) {
    return {
      asBoolean() {
        switch (typeof value) {
          case 'boolean':
            return value;
          case 'string':
            return ['1', 'true', 't', 'yes', 'y', 'on'].indexOf(value.toLowerCase()) >= 0;
          default:
            return !!value;
        }
      },
      asNumber() {
        const num = Number(value);
        return isNaN(num) ? 0 : num;
      },
      asString() { return value as string; },
      getSource() { return 'static'; }
    } as firebase.remoteConfig.Value;
  }

}
